import { categoriesRegExp } from './pages/Education/categories'
import loadable from '@loadable/component'
import {
  getBlogPostBySlug,
  getPageBySlug,
  getBlogPostsByPage,
  getPagesByCategory,
  getEducationArticles,
} from 'js/wpapi'
import { sortByIndex, isFeatured } from 'js/utils'
import categories from 'js/pages/Education/categories.js'
import { getCache, runQuery } from 'js/dato'
import { query as nativeAdQuery } from 'js/pages/nativeAdQuery'
import { query as redeemPageQuery } from 'js/pages/redeemPageQuery'
import { query as landingPageQuery } from 'js/pages/DatoPage/query'
import { languages } from 'js/locale/constants'

function getBlogCategoryPage({ match }) {
  if (match.params.category)
    return getBlogPostsByPage(
      parseInt(match.params.pageNumber, 10) || 1,
      'category_name',
      match.params.category
    )
  if (match.params.author)
    return getBlogPostsByPage(
      parseInt(match.params.pageNumber, 10) || 1,
      'author',
      match.params.author
    )
  if (match.params.tag)
    return getBlogPostsByPage(
      parseInt(match.params.pageNumber, 10) || 1,
      'tag',
      match.params.tag
    )
}

function getCategoryPages({ match }) {
  const category = match.params[0]
  const subCategory = match.params.category

  return getPagesByCategory({ category, subCategory })
}

const articlesObject = {}

Object.keys(categories).forEach(category => {
  articlesObject[category] = []
})

const Home = loadable(() =>
  import(/* webpackChunkName: "Home" */ './pages/Home/Home')
)
const SignUpPage = loadable(() =>
  import(/* webpackChunkName: "SignUpPage" */ './pages/SignUpPage')
)
const BlogPostPage = loadable(() =>
  import(/* webpackChunkName: "BlogPostPage" */ './pages/Blog/BlogPostPage')
)
const ArticlePage = loadable(() =>
  import(
    /* webpackChunkName: "ArticlePage" */ './pages/CMSTemplates/ArticlePage'
  )
)
const NativeAdsRouter = loadable(() =>
  import(
    /* webpackChunkName: "NativeAdsRouter" */ './components/NativeAdsRouter'
  )
)
const BlogStartPage = loadable(() =>
  import(/* webpackChunkName: "BlogStartPage" */ 'js/pages/Blog/BlogStartPage')
)
const BlogCategoryPage = loadable(() =>
  import(
    /* webpackChunkName: "BlogCategoryPage" */ 'js/pages/Blog/BlogCategoryPage'
  )
)
const CategoryPage = loadable(() =>
  import(
    /* webpackChunkName: "CategoryPage" */ 'js/pages/CMSTemplates/CategoryPage'
  )
)
const EducationStartPage = loadable(() =>
  import(
    /* webpackChunkName: "EducationStartPage" */ 'js/pages/Education/EducationStartPage'
  )
)
const PremiumPage = loadable(() =>
  import(
    /* webpackChunkName: "PremiumPage" */ './pages/PremiumPage/PremiumPage'
  )
)
const PrivacyPolicy = loadable(() =>
  import(/* webpackChunkName: "PrivacyPolicy" */ './pages/policy/PrivacyPolicy')
)
const PrivacyPolicyUS = loadable(() =>
  import(
    /* webpackChunkName: "PrivacyPolicyUS" */ './pages/policy/PrivacyPolicyUS'
  )
)
const PrivacyPolicyDE = loadable(() =>
  import(
    /* webpackChunkName: "PrivacyPolicyDE" */ './pages/policy/PrivacyPolicyDE'
  )
)
const MealPlansPage = loadable(() =>
  import(
    /* webpackChunkName: "MealPlansPage" */ './pages/MealPlansPage/MealPlansPage'
  )
)

const GetInContact = loadable(() =>
  import(/* webpackChunkName: "GetInContact" */ './pages/GetInContact')
)

const CaliforniaPrivacyRightsPage = loadable(() =>
  import(
    /* webpackChunkName: "CaliforniaPrivacyRightsPage" */ './pages/CaliforniaPrivacyRightsPage'
  )
)

const DatoNativeAd = loadable(() => import('./pages/DatoNativeAd'))
const DatoRedeemPage = loadable(() => import('./pages/Redeem/DatoRedeemPage'))
const DatoPage = loadable(() => import('./pages/DatoPage/DatoPage'))

const routes = [
  {
    path: `/`,
    component: Home,
    exact: true,
    translated: true,
  },
  {
    path: '/signup',
    component: SignUpPage,
  },
  {
    path: '/contact',
    component: GetInContact,
  },
  {
    path: '/californiaprivacyrights',
    component: CaliforniaPrivacyRightsPage,
  },
  {
    path: '/policy',
    component: PrivacyPolicy,
  },
  {
    path: '/policy-us',
    component: PrivacyPolicyUS,
  },
  {
    path: '/policy-de',
    component: PrivacyPolicyDE,
  },
  {
    path: '/:slug(three-week-weight-loss|sugar-detox|paleo)',
    // eslint-disable-next-line
    component: MealPlansPage,
    translated: true,
    getInitialProps: ({ match, language }) => {
      return import(
        `js/pages/MealPlansPage/data/${match.params.slug}-${
          language !== 'no' ? language : 'en-US'
        }.json`
      ).then(({ default: data }) => {
        return data[0].fields
      })
    },
  },
  { path: '/premium', component: PremiumPage },
  {
    path: '(/blog|/blog/page/:slug?)',
    component: BlogStartPage,
    exact: true,
    getInitialProps: ({ match }) => {
      return getBlogPostsByPage(match.params.slug || 1)
    },
  },
  {
    path: '/blog/page/:slug',
    component: BlogStartPage,
    getInitialProps: ({ match }) => {
      return getBlogPostsByPage(match.params.slug || 1)
    },
  },
  {
    path: '/blog/author/:author/:page?/:pageNumber?',
    component: BlogCategoryPage,
    getInitialProps: getBlogCategoryPage,
  },
  {
    path: '/blog/category/:category/:page?/:pageNumber?',
    component: BlogCategoryPage,
    getInitialProps: getBlogCategoryPage,
  },
  {
    path: '/blog/tags/:tag/:page?/:pageNumber?',
    component: BlogCategoryPage,
    getInitialProps: getBlogCategoryPage,
  },
  {
    path: '/blog/:slug',
    component: BlogPostPage,
    getInitialProps: ({ match }) => getBlogPostBySlug(match.params.slug),
  },
  {
    path: '/news/:slug([a-z-]+dato)',
    component: DatoNativeAd,
    getInitialProps: ({ match, language }) => {
      const slug = match.params.slug.replace('-dato', '')

      return getCache(`news/${slug}`, language)
        .catch(() => runQuery(nativeAdQuery(slug, language)))
        .then(({ nativeAdPage }) => nativeAdPage)
    },
  },
  {
    path: ['/redeem/:slug', '/influencer/:slug'],
    component: DatoRedeemPage,
    getInitialProps: ({ match, language }) => {
      const slug = match.params.slug
      return runQuery(redeemPageQuery(slug, language)).then(
        ({ redeemPage }) => redeemPage
      )
    },
  },
  {
    path: ['/(news|health)/:slug', '/:slug(ketoapp)'],
    component: NativeAdsRouter,
    translated: true,
    getInitialProps: ({ match }) => {
      return import('js/contentful').then(({ getEntries }) =>
        getEntries({
          content_type: 'marketingPage',
          'fields.slug': match.params.slug,
          locale: 'en-US',
        })
          .then(entries => {
            if (entries.total === 0) {
              throw new Error('Page not found')
            }

            return Promise.resolve({
              contentful: true,
              ...entries.items[0].fields,
            })
          })
          .catch(() => {
            return getPageBySlug('marketing', match.params.slug)
          })
      )
    },
  },
  {
    path: '/(news|health)/:slug',
    component: NativeAdsRouter,
    translated: true,
    getInitialProps: ({ match }) => {
      return import('js/contentful').then(({ getEntries }) =>
        getEntries({
          content_type: 'marketingPage',
          'fields.slug': match.params.slug,
          locale: 'en-US',
        })
          .then(entries => {
            if (entries.total === 0) {
              throw new Error('Page not found')
            }

            return Promise.resolve({
              contentful: true,
              ...entries.items[0].fields,
            })
          })
          .catch(() => {
            return getPageBySlug('marketing', match.params.slug)
          })
      )
    },
  },
  {
    path: '/health-education',
    component: EducationStartPage,
    exact: true,
    getInitialProps: () => {
      return new Promise(resolve =>
        getEducationArticles().then(articles => {
          articles = articles.sort(sortByIndex)

          resolve({
            featuredArticles: articles.filter(article => isFeatured(article)),
            articles: articles
              .filter(article => !isFeatured(article))
              .reduce((categories, article) => {
                if (
                  article &&
                  article.acf &&
                  article.acf.category &&
                  typeof categories[article.acf.category] === 'undefined'
                )
                  return categories

                categories[article.acf.category].push(article)
                return categories
              }, articlesObject),
          })
        })
      )
    },
  },
  { path: '/blog/tags/:tag/:page?/:pageNumber?', component: BlogCategoryPage },
  { path: '/blog/:slug', component: BlogPostPage },
  {
    path: '/news/:slug(demo-page-dato)',
    component: DatoNativeAd,
    translated: true,
  },
  {
    path: [
      '/page/:slug',
      '/:slug(about|press|lifesum-games|disclaimer|ambassadorswe)',
    ],
    component: DatoPage,
    translated: true,
    getInitialProps: ({ match, language }) => {
      const locale = languages.includes(language) ? language : 'en'

      return runQuery(
        landingPageQuery({ slug: match.params.slug, locale })
      ).then(({ page, fallback }) => {
        return { page, fallback }
      })
    },
  },
  { path: '/health/:slug', component: NativeAdsRouter },
  {
    path: '/(fitness|food)',
    exact: true,
    component: CategoryPage,
    getInitialProps: getCategoryPages,
  },
  { path: '/health-education', component: EducationStartPage, exact: true },
  {
    path: `/(health-education)/:category(${categoriesRegExp})`,
    component: CategoryPage,
    getInitialProps: getCategoryPages,
  },
  {
    path: '/:category/:slug',
    component: ArticlePage,
    getInitialProps: ({ match }) => {
      return getPageBySlug(match.params.category, match.params.slug)
    },
  },
  {
    path: '/:slug',
    component: ArticlePage,
    getInitialProps: ({ match }) => {
      return getPageBySlug(null, match.params.slug)
    },
  },
]

export default routes
